/**
 * Kiểu dữ liệu chia sẻ giữa client và DB.
 * Đồng bộ với supabase/migrations/001_initial_schema.sql
 */

export type UserRole = 'user' | 'truong_phong' | 'admin_luong' | 'admin_he_thong'

export type EmployeeStatus =
  | 'dang_lam_viec'
  | 'luan_chuyen'
  | 'da_nghi_huu'
  | 'da_nghi_viec'

export type DepartmentSalaryType = 'hanh_chinh' | 'lai_xe' | 'bao_ve_ky_thuat'

export interface Department {
  id: string
  code: string
  name: string
  parent_id: string | null
  salary_type: DepartmentSalaryType
  is_active: boolean
}

export interface Employee {
  id: string
  cccd: string
  ho_ten: string
  department_id: string | null
  chuc_vu: string | null
  ngach_code: string | null
  bac: number | null
  status: EmployeeStatus
}

export interface AppUser {
  id: string
  cccd: string
  employee_id: string | null
  role: UserRole
  department_id: string | null
  must_change_password: boolean
  failed_attempts: number
  locked_until: string | null
  /** Số lần bị khoá lũy tích (escalated lockout — migration 009). */
  lockout_count?: number | null
  is_active: boolean
  last_login_at: string | null
}

export interface AuthProfile {
  user: AppUser
  employee: Employee | null
  department: Department | null
}

export const ROLE_LABEL: Record<UserRole, string> = {
  user: 'Nhân viên',
  truong_phong: 'Trưởng phòng',
  admin_luong: 'Quản trị Lương',
  admin_he_thong: 'Quản trị Hệ thống',
}

export const STATUS_LABEL: Record<EmployeeStatus, string> = {
  dang_lam_viec: 'Đang làm việc',
  luan_chuyen: 'Luân chuyển',
  da_nghi_huu: 'Đã nghỉ hưu',
  da_nghi_viec: 'Đã nghỉ việc',
}

export const DEPT_TYPE_LABEL: Record<DepartmentSalaryType, string> = {
  hanh_chinh: 'Hành chính',
  lai_xe: 'Lái xe',
  bao_ve_ky_thuat: 'Bảo vệ / Kỹ thuật',
}

// =============================================================
// Salary grade & config
// =============================================================

export interface SalaryGrade {
  id: string
  ngach_code: string
  ngach_name: string
  bac: number
  he_so: number
  is_bac_cuoi: boolean
  thoi_gian_cho_thang: number
  chu_ky_nang_bac_nam: number
}

export interface SalaryConfig {
  id: string
  effective_date: string
  luong_co_so: number
  tien_an_trua_max: number
  giam_tru_ban_than: number
  giam_tru_phu_thuoc: number
  ty_le_bh_nld: number
  ty_le_bh_dv: number
  ty_le_cong_doan_phi: number
  so_ngay_cong_chuan: number
  he_so_lam_dem: number
  don_gia_km_lai_xe: number
  don_gia_an_trua_ngay: number
  /** Mức lương NLĐ hợp đồng 235 (LCB = hệ số × mức này); null = dùng lương cơ sở. Migration 029. */
  muc_luong_hd235?: number | null
  note: string | null
  created_by: string | null
  created_at: string
}

// Mở rộng Employee với toàn bộ trường có trong DB
/** Lịch sử công tác (v0.39): từ tháng from_month (ngày 01) thuộc phòng department_id. */
export interface EmployeeDeptHistory {
  id: string
  employee_id: string
  department_id: string
  from_month: string
  note: string | null
  created_at: string
}

/** Diễn biến lương (v0.47, migration 049) — mốc mới nhất có ngày hưởng ≤ hôm nay = lương hiện tại. */
export type SalaryHistoryLoai = 'khoi_tao' | 'nang_bac' | 'nang_truoc_han' | 'chuyen_ngach' | 'tnvk' | 'dieu_chinh' | 'cap_nhat'

export interface EmployeeSalaryHistory {
  id: string
  employee_id: string
  /** null = chưa rõ ngày (mốc Khởi tạo của hồ sơ chưa có ngày hưởng bậc) */
  ngay_huong: string | null
  loai: SalaryHistoryLoai
  ngach_code: string | null
  bac: number | null
  he_so: number | null
  tnvk_percent: number
  he_so_pccv: number
  so_qd: string | null
  ngay_qd: string | null
  ghi_chu: string | null
  created_at: string
}

/** Danh sách chi thưởng lễ, Tết (v0.47, migration 050). */
export interface BonusBatch {
  id: string
  ten_dot: string
  dip: string
  ngay_chi: string | null
  muc_mac_dinh: number
  status: 'draft' | 'approved'
  ghi_chu: string | null
  created_at: string
  approved_at: string | null
}

export interface BonusItem {
  id: string
  batch_id: string
  employee_id: string
  department_id: string | null
  muc_chi: number
  ghi_chu: string | null
}

export interface EmployeeFull extends Employee {
  ngay_sinh: string | null
  gioi_tinh: string | null
  dia_chi: string | null
  so_dien_thoai: string | null
  email: string | null
  ngay_huong_bac: string | null
  tnvk_percent: number
  he_so_pccv: number
  he_so_pctn: number
  so_nguoi_phu_thuoc: number
  /** Khoán điện thoại/tháng (KCT) — v0.17.0: tự áp dụng các tháng kế tiếp tới khi đổi. */
  khoan_dien_thoai: number
  /** Khoán công tác phí/tháng (KCT) — v0.17.0: tự áp dụng các tháng kế tiếp tới khi đổi. */
  khoan_cong_tac_phi: number
  /** Định mức phép năm (ngày/năm) — v0.25, mặc định 12. */
  phep_nam_dinh_muc: number
  so_tai_khoan: string | null
  ten_ngan_hang: string | null
  ngay_vao_lam: string | null
  ngay_thay_doi_status: string | null
  ghi_chu: string | null
}

// =============================================================
// Attendance
// =============================================================
export type AttendanceStatus = 'draft' | 'pending' | 'locked'

export const ATT_STATUS_LABEL: Record<AttendanceStatus, string> = {
  draft: 'Nháp',
  pending: 'Chờ duyệt',
  locked: 'Đã chốt',
}

export interface AttendanceOffice {
  id: string
  employee_id: string
  department_id: string
  month_year: string
  cong_tg: number
  cong_t7cn: number
  ngoai_gio: number
  cong_vd: number
  le_tet_hoc_phep: number
  le_tet_di_lam: number
  status: AttendanceStatus
  ghi_chu: string | null
}

export interface AttendanceSecurity {
  id: string
  employee_id: string
  department_id: string
  month_year: string
  cong_ngay: number
  cong_dem: number
  cong_t7cn: number
  ngoai_gio: number
  le_tet_di_lam: number
  le_tet_hoc_phep: number
  status: AttendanceStatus
  ghi_chu: string | null
}

export interface AttendanceTechnician {
  id: string
  employee_id: string
  department_id: string
  month_year: string
  cong_tg: number
  cong_t7cn: number
  ngoai_gio: number
  le_tet_di_lam: number
  le_tet_hoc_phep: number
  status: AttendanceStatus
  ghi_chu: string | null
}

export type DriverSource = 'truong_phong' | 'admin_luong'

// =============================================================
// Salary records (Module 4)
// =============================================================
export type SalaryRecordStatus = 'draft' | 'pending' | 'approved' | 'cancelled'

export const SR_STATUS_LABEL: Record<SalaryRecordStatus, string> = {
  draft: 'Nháp',
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt',
  cancelled: 'Đã huỷ',
}

export interface SalaryRecord {
  id: string
  employee_id: string
  department_id: string
  month_year: string
  // Snapshot identity
  cccd: string
  ho_ten: string
  chuc_vu: string | null
  ngach_code: string | null
  bac: number | null
  he_so_bac: number | null
  tnvk_percent: number | null
  he_so_pccv: number | null
  he_so_pctn: number | null
  // Snapshot config
  luong_co_so: number | null
  lcb_ngay: number | null
  // Chịu thuế
  tien_pccv_pctn: number | null
  tien_luong_ct: number | null
  thuong: number | null
  tong_chiu_thue: number | null
  // Không chịu thuế
  tien_luong_kct: number | null
  an_trua: number | null
  xang_xe: number | null
  dien_thoai: number | null
  cong_tac_phi: number | null
  tong_khong_ct: number | null
  // BH & thuế
  co_so_trich: number | null
  trich_nld: number | null
  don_vi_dong: number | null
  cdp: number | null
  thue_tncn: number | null
  truy_thu: number | null
  truy_linh: number | null
  thuc_linh: number | null
  // Banking
  so_tai_khoan: string | null
  ten_ngan_hang: string | null
  raw_data: Record<string, unknown> | null
  // Workflow
  status: SalaryRecordStatus
  approved_by: string | null
  approved_at: string | null
  cancelled_by: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface AttendanceDriver {
  id: string
  employee_id: string
  department_id: string
  month_year: string
  source: DriverSource
  s600: number
  xe_4_7: number
  xe_16_29: number
  cong_mia: number
  nhan_cong: number
  cong_cho: number
  so_km: number
  cong_t7cn: number
  ngoai_gio: number
  le_tet_di_lam: number
  le_tet_hoc_phep: number
  status: AttendanceStatus
  is_matched: boolean
  ghi_chu: string | null
}

// =============================================================
// Activity logs (Module 5 — phiên 8)
// =============================================================

export interface ActivityLog {
  id: string
  user_id: string | null
  user_cccd: string | null
  /** Định dạng `<op>.<table>`: insert.departments, update.users, delete.attendance_driver, ... */
  action: string
  entity_type: string | null
  entity_id: string | null
  old_value: Record<string, unknown> | null
  new_value: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  description: string | null
  created_at: string
}

/** Tách action `<op>.<table>` thành `{ op, table }`. Phòng trường hợp action không có dấu chấm. */
export function parseLogAction(action: string): { op: string; table: string } {
  const i = action.indexOf('.')
  if (i < 0) return { op: action, table: '' }
  return { op: action.slice(0, i), table: action.slice(i + 1) }
}

export const LOG_OP_LABEL: Record<string, string> = {
  insert: 'Thêm mới',
  update: 'Cập nhật',
  delete: 'Xoá',
  login: 'Đăng nhập',
  logout: 'Đăng xuất',
}

/** Bảng audit từ migration 005/006 phủ 9 bảng (users đã DROP). Dùng cho dropdown filter. */
export const LOG_ENTITY_TYPES = [
  'departments',
  'employees',
  'salary_config',
  'salary_grades',
  'attendance_office',
  'attendance_driver',
  'attendance_security',
  'attendance_technician',
  'salary_records',
  'pinned_notes',
  'news_posts',
  'leave_requests',
  'users',
] as const

// =============================================================
// Quản lý nghỉ phép (v0.25, migration 023)
// =============================================================

export type LeaveType = 'phep_nam' | 'om' | 'viec_rieng' | 'khac'
export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  phep_nam: 'Phép năm',
  om: 'Nghỉ ốm',
  viec_rieng: 'Việc riêng',
  khac: 'Khác',
}

export const LEAVE_STATUS_LABEL: Record<LeaveStatus, string> = {
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
  cancelled: 'Đã huỷ',
}

export interface LeaveRequest {
  id: string
  employee_id: string
  department_id: string | null
  /** Snapshot lúc gửi đơn. */
  employee_name: string | null
  type: LeaveType
  from_date: string
  to_date: string
  so_ngay: number
  reason: string | null
  status: LeaveStatus
  decided_by: string | null
  decided_by_name: string | null
  decided_at: string | null
  decide_note: string | null
  created_at: string
  updated_at: string
}

export interface LeaveBalance {
  dinh_muc: number
  da_dung: number
  con_lai: number
}

// =============================================================
// Bản tin đơn vị (v0.23, migration 021 · phân khối v0.24, migration 022)
// =============================================================

/** 3 khối của bản tin (v0.24). */
export type NewsCategory = 'chinh_quyen' | 'dang' | 'cong_doan'

export const NEWS_CATEGORY_LABEL: Record<NewsCategory, string> = {
  chinh_quyen: 'Chính quyền',
  dang: 'Đảng',
  cong_doan: 'Công đoàn',
}

export interface NewsPost {
  id: string
  title: string
  content: string
  /** Đường dẫn ảnh bìa trong bucket news-images (signed URL khi hiển thị). */
  cover_path: string | null
  is_pinned: boolean
  category: NewsCategory
  created_by: string | null
  created_by_name: string | null
  created_at: string
  updated_at: string
}

// =============================================================
// Module 6 — Trao đổi nội bộ + Ghi chú ghim (v0.20, migration 019)
// =============================================================

export interface ChatChannel {
  id: string
  code: string
  name: string
  type: 'general' | 'department' | 'dm'
  department_id: string | null
  /** GĐ3 — kênh nhắn riêng: 2 thành viên (a < b). */
  dm_user_a?: string | null
  dm_user_b?: string | null
  is_active: boolean
}

/** GĐ3 — danh bạ nội bộ (RPC chat_directory) cho picker DM + @mention. */
export interface DirectoryEntry {
  user_id: string
  display_name: string
  dept_name: string | null
}

export interface ChatMessage {
  id: string
  channel_id: string
  sender_id: string
  /** Snapshot lúc gửi — không join users/employees khi đọc (RLS). */
  sender_cccd: string | null
  sender_name: string | null
  content: string
  reply_to: string | null
  created_at: string
  edited_at: string | null
  deleted_at: string | null
  // GĐ2 — đính kèm (1 file/tin, bucket chat-attachments)
  attachment_path: string | null
  attachment_name: string | null
  attachment_type: string | null
  attachment_size: number | null
  /** GĐ3 — danh sách user_id được @nhắc trong tin. */
  mentions: string[] | null
}

/** GĐ2 — bày tỏ cảm xúc. */
export const REACTION_EMOJIS = ['👍', '❤️', '✅', '😄'] as const
export type ReactionEmoji = (typeof REACTION_EMOJIS)[number]

export interface ChatReaction {
  message_id: string
  channel_id: string
  user_id: string
  user_name: string | null
  emoji: ReactionEmoji
  created_at: string
}

/** GĐ2 — xác nhận đã đọc ghi chú ghim. */
export interface NoteAck {
  note_id: string
  user_id: string
  user_name: string | null
  ack_at: string
}

export type NoteCategory = 'thong_bao' | 'viec_can_lam' | 'lich'

export const NOTE_CATEGORY_LABEL: Record<NoteCategory, string> = {
  thong_bao: 'Thông báo',
  viec_can_lam: 'Việc cần làm',
  lich: 'Lịch',
}

export interface PinnedNote {
  id: string
  scope: 'all' | 'department'
  department_id: string | null
  title: string
  content: string
  category: NoteCategory
  priority: number
  effective_until: string | null
  /** GĐ2 — yêu cầu thành viên bấm "xác nhận đã đọc". */
  require_ack: boolean
  created_by: string | null
  created_by_name: string | null
  created_at: string
  updated_at: string
}

// =============================================================
// Thanh toán Mia (v0.29, migration 026)
// =============================================================

export type MiaBatchStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'paid'
export type MiaHinhThucTT = 'tien_mat' | 'ngan_hang' | 'khac'

export const MIA_STATUS_LABEL: Record<MiaBatchStatus, string> = {
  draft: 'Nháp',
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
  paid: 'Đã thanh toán',
}

export const MIA_HINH_THUC_LABEL: Record<MiaHinhThucTT, string> = {
  tien_mat: 'Tiền mặt',
  ngan_hang: 'Ngân hàng',
  khac: 'Khác',
}

export interface MiaPaymentBatch {
  id: string
  employee_id: string
  department_id: string | null
  employee_name: string | null
  doi: string | null
  mac_xe: string | null
  bien_so: string | null
  gddd_so: string | null
  mia_dot: string
  tu_ngay: string
  den_ngay: string
  ly_do: string | null
  tam_ung_da_nhan: number
  hinh_thuc_tt: MiaHinhThucTT
  so_tai_khoan: string | null
  ten_ngan_hang: string | null
  status: MiaBatchStatus
  submitted_at: string | null
  decided_by: string | null
  decided_by_name: string | null
  decided_at: string | null
  decide_note: string | null
  paid_at: string | null
  paid_note: string | null
  created_at: string
  updated_at: string
}

export interface MiaFuelItem {
  id: string
  batch_id: string
  so_hoa_don: string | null
  ngay: string | null
  don_vi_ban: string | null
  so_luong_lit: number | null
  thue_gtgt: number | null
  tong_cong: number
  ghi_chu: string | null
  created_at: string
  updated_at: string
}

export interface MiaTollItem {
  id: string
  batch_id: string
  so_lenh: string | null
  thang_thuc_hien: string | null
  tuyen_duong: string | null
  ve_etc_sl: number | null
  ve_etc_tien: number | null
  ve_giay_sl: number | null
  ve_giay_tien: number | null
  /** Cột generated ở DB = ve_etc_tien + ve_giay_tien. */
  tong_tien: number
  ghi_chu: string | null
  created_at: string
  updated_at: string
}

export interface MiaOvernightItem {
  id: string
  batch_id: string
  noi_cong_tac: string | null
  tu_ngay: string | null
  den_ngay: string | null
  so_dem: number
  phu_cap_dem: number
  /** Cột generated ở DB = so_dem * phu_cap_dem. */
  tong_tien: number
  ghi_chu: string | null
  created_at: string
  updated_at: string
}

export type MiaInvoiceItemTable = 'batch' | 'fuel' | 'toll' | 'overnight'

export interface MiaInvoiceFile {
  id: string
  batch_id: string
  item_table: MiaInvoiceItemTable
  item_id: string | null
  storage_path: string
  file_name: string
  file_type: string | null
  file_size: number | null
  extracted: Record<string, unknown> | null
  uploaded_by: string | null
  created_at: string
}

export interface MiaBatchTotals {
  tien_nhien_lieu: number
  tien_ve_cau: number
  tien_luu_dem: number
  tam_ung_da_nhan: number
  so_tien_duoc_linh: number
  so_hoa_don_kem: number
}

// =============================================================
// VĂN PHÒNG CÁ NHÂN — VĂN BẢN ĐẾN (v0.33, migration 027)
// =============================================================

export interface OfficeDocCategory {
  id: string
  owner_id: string
  name: string
  keywords: string[]
  color: string
  sort_order: number
  created_at: string
}

export interface OfficeIncomingDoc {
  id: string
  owner_id: string
  category_id: string | null
  file_name: string
  original_name: string | null
  storage_path: string
  file_type: string | null
  file_size: number | null
  uploaded_at: string
  noi_ban_hanh: string | null
  so_ky_hieu: string | null
  ngay_van_ban: string | null
  trich_yeu: string | null
  deadline: string | null
  deadline_note: string | null
  /** Chỉ có khi nạp riêng (listOfficeDocTexts) — danh sách không kéo cột này. */
  content_text?: string | null
  note: string | null
  is_done: boolean
  done_at: string | null
  updated_at: string
}

/** Văn bản trả lời / kết quả xử lý gắn với văn bản đến (migration 028). */
export interface OfficeDocReply {
  id: string
  doc_id: string
  owner_id: string
  file_name: string
  storage_path: string
  file_type: string | null
  file_size: number | null
  uploaded_at: string
}

// =============================================================
// CHẤM CÔNG THEO NGÀY + ĐIỂM DANH GPS (v0.35, migration 030)
// =============================================================

export interface AttendanceSite {
  id: string
  name: string
  lat: number
  lng: number
  radius_m: number
  /** IP công cộng mạng cơ quan — điểm danh từ các IP này hợp lệ không cần GPS (v0.37). */
  allowed_ips: string[]
  is_active: boolean
  created_at: string
}

export interface AttendanceHoliday {
  ngay: string
  ten: string
}

export type CheckinKind = 'in' | 'out' | 'form'

export interface AttendanceCheckin {
  id: string
  employee_id: string | null
  ngay: string
  checked_at: string
  kind: CheckinKind
  lat: number | null
  lng: number | null
  accuracy_m: number | null
  site_id: string | null
  distance_m: number | null
  is_valid: boolean
  source: 'app' | 'google_form' | 'import'
  raw_name: string | null
  raw_email: string | null
  raw_unit: string | null
  device_id: string | null
  device_kind: 'mobile' | 'desktop' | null
  client_ip: string | null
  via: 'gps' | 'ip' | null
  /** 'shared_device' | 'new_device' (v0.37) */
  flags: string[]
  shared_with: string[]
  /** v0.38 — điểm danh ngoài (công tác, đi đoàn, đi họp, cho thuê…) chờ TP duyệt */
  remote: boolean
  remote_reason: RemoteReason | null
  remote_note: string | null
  review_status: 'pending' | 'approved' | 'rejected' | null
  reviewed_at: string | null
  created_at: string
}

export type RemoteReason = 'cong_tac' | 'di_doan' | 'di_hop' | 'cho_thue' | 'khac'

export interface AttendanceDailyRow {
  id?: string
  employee_id: string
  department_id: string | null
  ngay: string
  ky_hieu: string | null
  ngoai_gio: number
  so_km: number
  ghi_chu?: string | null
}

export interface AttendanceSyncConfig {
  id: number
  spreadsheet_id: string | null
  sheet_name: string
  enabled: boolean
  last_synced_at: string | null
  last_result: Record<string, unknown> | null
  /** v0.37.1 — bật: máy tính chỉ qua mạng cơ quan + từ chối GPS sai số > 150 m */
  strict_checkin: boolean
}

// ─── Sổ giao ca bảo vệ (v0.40, migration 038) ───────────────────────────────

export interface GuardEquipment {
  item: string
  qty: number | null
  status: 'du' | 'hong' | 'thieu'
  note: string
}

export interface GuardShiftLog {
  id: string
  ngay: string
  ca: 1 | 2 | 3
  muc_tieu: string
  giao_ten: string | null
  nhan_ten: string | null
  equipment: GuardEquipment[]
  an_ninh: string | null
  khach_ra_vao: string | null
  vat_tu: string | null
  ton_dong: string | null
  chi_dao: string | null
  sign_giao_ten: string | null
  sign_giao_at: string | null
  sign_nhan_ten: string | null
  sign_nhan_at: string | null
  sign_duyet_ten: string | null
  sign_duyet_at: string | null
  created_at: string
  updated_at: string
}

// ─── Lịch trực bảo vệ (v0.41, migration 040) ────────────────────────────────

export interface GuardRoster {
  id: string
  month_year: string
  muc_tieu: string
  members: string[]
  /** { employee_id: { "01": "1" | "2" | "3" | "HC" | "N" | "P", … } } */
  cells: Record<string, Record<string, string>>
  note: string | null
  status: 'draft' | 'published'
  published_at: string | null
  updated_at: string
}

export interface GuardMember {
  id: string
  ho_ten: string
  chuc_vu: string | null
}
